import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Trash2, UserPlus, MapPin, Save, ListPlus } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function Departments() {
  const { activeWorkspaceId } = useAuth();
  const [tab, setTab] = useState("departments");
  return (
    <>
      <PageHeader
        eyebrow="HR & Org"
        title="Departments & Locations"
        description="Group team members by department (a person can belong to several). Set up company locations for region-specific announcements."
        testid="departments-header"
      />
      <div className="px-4 sm:px-6 md:px-10 mb-3">
        <div className="border-b border-border flex gap-0">
          <button onClick={() => setTab("departments")} className={`px-4 py-2 text-sm border-b-2 inline-flex items-center gap-2 ${tab === "departments" ? "border-primary font-bold" : "border-transparent text-muted-foreground"}`} data-testid="depts-tab-departments">
            <Building2 className="w-3.5 h-3.5" /> Departments
          </button>
          <button onClick={() => setTab("locations")} className={`px-4 py-2 text-sm border-b-2 inline-flex items-center gap-2 ${tab === "locations" ? "border-primary font-bold" : "border-transparent text-muted-foreground"}`} data-testid="depts-tab-locations">
            <MapPin className="w-3.5 h-3.5" /> Locations
          </button>
        </div>
      </div>
      <div className="px-4 sm:px-6 md:px-10 pb-10">
        {tab === "departments" && <DepartmentsTab wsId={activeWorkspaceId} />}
        {tab === "locations" && <LocationsTab wsId={activeWorkspaceId} />}
      </div>
    </>
  );
}

function DepartmentsTab({ wsId }) {
  const { memberships, activeWorkspaceId } = useAuth();
  const { can } = useWorkspacePerms();
  const _membership = memberships?.find((m) => m.workspace_id === activeWorkspaceId);
  const canManage = ["owner", "admin", "workspace_owner", "workspace_admin"].includes(_membership?.role) || can("department.manage");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null);
  const [managingMembers, setManagingMembers] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    if (!wsId) return;
    setBusy(true);
    try { const { data } = await api.get(`/workspaces/${wsId}/departments`); setRows(data || []); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [wsId]);

  const del = async (d) => {
    if (!confirm(`Delete department "${d.name}"? Its member assignments will be removed.`)) return;
    try { await api.delete(`/departments/${d.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <>
      <div className="flex justify-end gap-2 mb-3">
        {canManage && (
          <>
            <Button variant="outline" onClick={() => setBulkOpen(true)} className="rounded-none" data-testid="depts-bulk-btn"><ListPlus className="w-4 h-4 mr-2" /> Bulk add</Button>
            <Button onClick={() => setEditing({})} className="rounded-none" data-testid="depts-new-btn"><Plus className="w-4 h-4 mr-2" /> Add department</Button>
          </>
        )}
      </div>
      {busy && rows.length === 0 ? <div className="text-sm">Loading…</div>
        : rows.length === 0 ? <EmptyState title="No departments yet" description="Group team members by function — Engineering, Design, Sales, HR, Finance, etc. One person can belong to multiple departments." icon={Building2} testid="depts-empty" />
        : (
          <div className="border border-border" data-testid="departments-table">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
              <div className="col-span-2">Code</div>
              <div className="col-span-4">Name</div>
              <div className="col-span-4">Description</div>
              <div className="col-span-1 text-right">Members</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {rows.map((d) => (
              <div key={d.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm" data-testid={`dept-row-${d.id}`}>
                <button onClick={() => setEditing(d)} className="col-span-2 text-left font-mono text-xs">{d.code || "—"}</button>
                <button onClick={() => setEditing(d)} className="col-span-4 font-semibold text-left">{d.name}</button>
                <div className="col-span-4 text-xs text-muted-foreground truncate">{d.description || "—"}</div>
                <button onClick={() => setManagingMembers(d)} className="col-span-1 text-right text-sm font-mono underline" data-testid={`dept-members-btn-${d.id}`}>{d.member_count}</button>
                <div className="col-span-1 flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => del(d)} data-testid={`dept-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      {editing && <DeptDialog wsId={wsId} dep={editing} onClose={() => setEditing(null)} onSaved={async () => { await load(); setEditing(null); }} />}
      {managingMembers && <DeptMembersDialog wsId={wsId} dep={managingMembers} onClose={() => setManagingMembers(null)} onChanged={load} />}
      {bulkOpen && <DeptBulkAddDialog wsId={wsId} existingNames={rows.map((r) => r.name)} onClose={() => setBulkOpen(false)} onSaved={async () => { await load(); setBulkOpen(false); }} />}
    </>
  );
}

function DeptDialog({ wsId, dep, onClose, onSaved }) {
  const isNew = !dep?.id;
  const [form, setForm] = useState({ name: dep.name || "", code: dep.code || "", description: dep.description || "", head_user_id: dep.head_user_id || "" });
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  useEffect(() => { api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])); }, [wsId]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, head_user_id: form.head_user_id || null };
      if (isNew) await api.post(`/workspaces/${wsId}/departments`, payload);
      else await api.patch(`/departments/${dep.id}`, payload);
      toast.success("Saved");
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="dept-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add department" : `Edit · ${dep.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="dept-name" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code" value={form.code} onChange={(v) => set("code", v)} testid="dept-code" />
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Department head</Label>
              <Select value={form.head_user_id || "_none"} onValueChange={(v) => set("head_user_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10" data-testid="dept-head"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="dept-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.name} className="rounded-none" data-testid="dept-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeptMembersDialog({ wsId, dep, onClose, onChanged }) {
  const [members, setMembers] = useState([]);
  const [wsMembers, setWsMembers] = useState([]);
  const [busy, setBusy] = useState(true);
  const [picking, setPicking] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const [m, ws] = await Promise.all([api.get(`/departments/${dep.id}/members`), api.get(`/workspaces/${wsId}/members`)]);
      setMembers(m.data || []);
      setWsMembers(ws.data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [dep.id]);

  const add = async () => {
    if (!picking) return;
    try { await api.post(`/departments/${dep.id}/members`, { user_id: picking }); toast.success("Added"); setPicking(""); await load(); onChanged?.(); }
    catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
  };
  const remove = async (uid) => {
    try { await api.delete(`/departments/${dep.id}/members/${uid}`); toast.success("Removed"); await load(); onChanged?.(); }
    catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
  };

  const eligible = wsMembers.filter((w) => !members.some((m) => m.user_id === w.user_id));

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="dept-members-dialog">
        <DialogHeader><DialogTitle>{dep.name} · Members</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Select value={picking} onValueChange={setPicking}>
              <SelectTrigger className="h-10 flex-1" data-testid="dept-add-member-pick"><SelectValue placeholder="Pick a workspace member…" /></SelectTrigger>
              <SelectContent>
                {eligible.length === 0 ? <div className="px-2 py-1.5 text-xs text-muted-foreground">All workspace members are already in this department.</div>
                  : eligible.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={add} disabled={!picking} className="rounded-none" data-testid="dept-add-member-btn"><UserPlus className="w-4 h-4 mr-2" /> Add</Button>
          </div>
          {busy && members.length === 0 ? <div className="text-sm">Loading…</div>
            : members.length === 0 ? <div className="text-sm text-muted-foreground">No members yet.</div>
            : (
              <div className="border border-border" data-testid="dept-members-table">
                {members.map((m) => (
                  <div key={m.user_id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border text-sm items-center" data-testid={`dept-member-${m.user_id}`}>
                    <div className="col-span-8 truncate">{m.user?.name || m.user?.email}</div>
                    <div className="col-span-3 text-xs text-muted-foreground">{m.is_primary ? "primary" : ""}</div>
                    <div className="col-span-1 text-right">
                      <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => remove(m.user_id)} data-testid={`dept-remove-${m.user_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LocationsTab({ wsId }) {
  const [rows, setRows] = useState([{ name: "", city: "", country: "" }]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/locations`);
      setRows(data?.length ? data : [{ name: "", city: "", country: "" }]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [wsId]);

  const setItem = (idx, k, v) => setRows((r) => { const out = [...r]; out[idx] = { ...out[idx], [k]: v }; return out; });
  const add = () => setRows((r) => [...r, { name: "", city: "", country: "" }]);
  const remove = (idx) => setRows((r) => r.filter((_, i) => i !== idx));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { locations: rows.filter((r) => r.name?.trim()).map((r) => ({ name: r.name, city: r.city || null, country: r.country || null })) };
      await api.patch(`/workspaces/${wsId}/locations`, payload);
      toast.success("Locations saved");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return loading ? <div className="text-sm">Loading…</div> : (
    <div className="space-y-3" data-testid="locations-editor">
      <div className="border border-border">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
          <div className="col-span-4">Name *</div>
          <div className="col-span-3">City</div>
          <div className="col-span-3">Country</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {rows.map((r, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border items-center" data-testid={`location-row-${idx}`}>
            <Input value={r.name} onChange={(e) => setItem(idx, "name", e.target.value)} placeholder="HQ" className="col-span-4 h-9 text-sm" data-testid={`location-name-${idx}`} />
            <Input value={r.city} onChange={(e) => setItem(idx, "city", e.target.value)} placeholder="Mumbai" className="col-span-3 h-9 text-sm" data-testid={`location-city-${idx}`} />
            <Input value={r.country} onChange={(e) => setItem(idx, "country", e.target.value)} placeholder="IN" className="col-span-3 h-9 text-sm" data-testid={`location-country-${idx}`} />
            <div className="col-span-2 text-right">
              <Button size="sm" variant="ghost" className="rounded-none" onClick={() => remove(idx)} data-testid={`location-remove-${idx}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between">
        <Button variant="outline" className="rounded-none" onClick={add} data-testid="location-add"><Plus className="w-4 h-4 mr-2" /> Add row</Button>
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="locations-save"><Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save locations"}</Button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid} />
    </div>
  );
}


// R268hhhh · Feb 2026 — Bulk-add departments by pasting names one per
// line. Shows a live preview split into "will add" vs "already
// exists" (case-insensitive) so users can adjust the paste before
// committing.
function DeptBulkAddDialog({ wsId, existingNames, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const parsed = (() => {
    const seen = new Set();
    const willAdd = [];
    const dupInBatch = [];
    const dupExisting = [];
    const existingLc = new Set((existingNames || []).map((n) => (n || "").trim().toLowerCase()));
    for (const raw of (text || "").split(/\r?\n/)) {
      const n = raw.trim();
      if (!n) continue;
      const lc = n.toLowerCase();
      if (seen.has(lc)) { dupInBatch.push(n); continue; }
      seen.add(lc);
      if (existingLc.has(lc)) dupExisting.push(n);
      else willAdd.push(n);
    }
    return { willAdd, dupInBatch, dupExisting };
  })();

  const submit = async () => {
    if (parsed.willAdd.length === 0) { toast.error("Nothing new to add"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/departments/bulk`, { names: parsed.willAdd });
      toast.success(`Added ${data.created_count} department${data.created_count === 1 ? "" : "s"}${data.skipped_count ? ` · ${data.skipped_count} skipped` : ""}`);
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk add failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="dept-bulk-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ListPlus className="w-4 h-4" /> Bulk add departments</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Paste department names — one per line</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              placeholder={"Engineering\nDesign\nSales\nHR\nFinance\n…"}
              className="font-mono text-sm"
              data-testid="dept-bulk-textarea"
            />
            <div className="mt-1 text-[11px] text-muted-foreground">
              Matching is case-insensitive. Up to 200 names at a time. Empty lines and duplicates are ignored.
            </div>
          </div>
          {(parsed.willAdd.length + parsed.dupExisting.length + parsed.dupInBatch.length) > 0 && (
            <div className="border border-border text-xs" data-testid="dept-bulk-preview">
              <div className="grid grid-cols-3 gap-0 divide-x divide-border">
                <div className="p-3">
                  <div className="label-eyebrow text-[10px] text-emerald-700 font-semibold mb-1.5">Will add · {parsed.willAdd.length}</div>
                  <ul className="space-y-0.5 max-h-40 overflow-auto">
                    {parsed.willAdd.length === 0 ? <li className="text-muted-foreground italic">Nothing yet</li> : parsed.willAdd.map((n, i) => <li key={i} className="font-mono truncate" title={n} data-testid={`dept-bulk-willadd-${i}`}>{n}</li>)}
                  </ul>
                </div>
                <div className="p-3">
                  <div className="label-eyebrow text-[10px] text-amber-700 font-semibold mb-1.5">Already exists · {parsed.dupExisting.length}</div>
                  <ul className="space-y-0.5 max-h-40 overflow-auto">
                    {parsed.dupExisting.length === 0 ? <li className="text-muted-foreground italic">—</li> : parsed.dupExisting.map((n, i) => <li key={i} className="font-mono truncate text-muted-foreground line-through" title={n} data-testid={`dept-bulk-existing-${i}`}>{n}</li>)}
                  </ul>
                </div>
                <div className="p-3">
                  <div className="label-eyebrow text-[10px] text-muted-foreground font-semibold mb-1.5">Duplicate in paste · {parsed.dupInBatch.length}</div>
                  <ul className="space-y-0.5 max-h-40 overflow-auto">
                    {parsed.dupInBatch.length === 0 ? <li className="text-muted-foreground italic">—</li> : parsed.dupInBatch.map((n, i) => <li key={i} className="font-mono truncate text-muted-foreground line-through" title={n}>{n}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || parsed.willAdd.length === 0} className="rounded-none" data-testid="dept-bulk-submit">
            {busy ? "Adding…" : `Add ${parsed.willAdd.length} department${parsed.willAdd.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
